// client/src/components/moonbeams/MoonbeamCreator.js
//
// Parent orchestrator for the Moonbeam creation + send flow.
//
// CHANGES IN THIS REVISION (Share button + link-only fast path):
//   - The Step 1 "Review & Share" button is now just "Share" (label change
//     lives in MoonbeamCreatorRecipients). To support the link-only fast
//     path, this parent now passes `onShareDirect={handleSend}` down to
//     Step 1 in addition to the existing `onNext`. When the user has ONLY
//     the shareable link enabled (no email), Step 1 calls onShareDirect to
//     run the real send immediately and skip the review screen. When email
//     is involved, Step 1 still calls onNext to go to the review screen
//     (Step 2) as before.
//   - handleSend itself is UNCHANGED in behavior — it already creates the
//     moonbeam, creates the link, sends/schedules email, writes the link to
//     the clipboard, and navigates to /my-moonbeams with the structured
//     shareSuccess descriptor. The link-only path just invokes it one screen
//     earlier. The MyMoonbeams modal keys off the same descriptor.
//   - Display text "eCard"→"Moonbeam" in the send-mode header and the
//     subscription-required error string. Routes, fields, and variables
//     are unchanged.
//
// PREVIOUS CHANGES (simple email form) — preserved:
//   - Derives `simpleEmailForm` from user.tier.simple_email_form and passes
//     it + fromName state down to Step 1.
//
// PREVIOUS CHANGES (recipientname fix, Email toggle gate, Shareable Links)
// — preserved.

import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api.js';
import moonbeamService from '../../services/moonbeamService.js';
import { useAuth } from '../../hooks/useAuth.js';

import {
  buildPreviewHtml, isUnlimitedUsage,
  ProgressSteps,
} from './MoonbeamCreatorShared.js';

import { BirthdaySwitchModal, MusicSelectorModal } from './MoonbeamCreatorModals.js';

import RecipientsAndTimingStep from './MoonbeamCreatorRecipients.js';
import FinalizeStep from './MoonbeamCreatorFinalize.js';

export default function MoonbeamCreator() {
  const { templateId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  const [template, setTemplate] = useState(null);
  const [musicOptions, setMusicOptions] = useState([]);
  const [siteName, setSiteName] = useState('Fairytale');

  const [mode, setMode] = useState('create');
  const [sendStep, setSendStep] = useState(1);

  const [selectedMusicId, setSelectedMusicId] = useState(null);
  const [showMusicModal, setShowMusicModal] = useState(false);

  const [templateData, setTemplateData] = useState({});
  const [templateFields, setTemplateFields] = useState([]);

  const templateDataRef = useRef(templateData);
  useEffect(() => { templateDataRef.current = templateData; }, [templateData]);

  const [recipients, setRecipients] = useState([]);

  const [deliveryMode, setDeliveryMode] = useState('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [birthdayTime, setBirthdayTime] = useState('09:00');

  const [birthdaySwitchModal, setBirthdaySwitchModal] = useState(null);

  // ─── SHAREABLE LINK STATE ───────────────────────────────────────────────
  const [linkEnabled, setLinkEnabled] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');

  // ─── EMAIL CHANNEL STATE ────────────────────────────────────────────────
  const [emailEnabled, setEmailEnabled] = useState(false);

  // ─── FROM NAME OVERRIDE ─────────────────────────────────────────────────
  const defaultFromName = user ? `${user.firstname || ''} ${user.lastname || ''}`.trim() : '';
  const [fromName, setFromName] = useState(defaultFromName);

  useEffect(() => {
    if (defaultFromName && !fromName) setFromName(defaultFromName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultFromName]);

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [validationError, setValidationError] = useState(null);

  const [usage, setUsage] = useState(null);

  const tier = user?.tier;
  const simpleEmailForm = !!tier?.simple_email_form;
  const isLoggedIn = !!user;
  const isFreeUser = isLoggedIn && !tier?.ispaid;
  const isTemplateFreeSendable = template?.isfree || false;
  const canSend = isLoggedIn && (isFreeUser ? isTemplateFreeSendable : tier?.cansend);
  const isBirthdayTemplate = template?.isbirthday || false;
  const isBirthdayDelivery = deliveryMode === 'birthday';

  const previewHtml = useMemo(
    () => (template ? buildPreviewHtml(template, selectedMusicId, musicOptions) : ''),
    [template, selectedMusicId, musicOptions]
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // VIEWPORT HEIGHT MESSAGING - Send available height to iframe for scaling
  // ═══════════════════════════════════════════════════════════════════════════
  const sendViewportHeight = () => {
    if (iframeRef.current?.contentWindow) {
      const navbarHeight = window.innerWidth < 640 ? 56 : 80;
      const toolbarHeight = 60;
      const availableHeight = window.innerHeight - navbarHeight - toolbarHeight;

      iframeRef.current.contentWindow.postMessage({
        type: 'setViewportHeight',
        height: availableHeight
      }, '*');
    }
  };

  useEffect(() => {
    const handleResize = () => {
      sendViewportHeight();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleIframeLoad = () => {
    sendViewportHeight();

    if (iframeRef.current?.contentWindow) {
      const data = templateDataRef.current || {};
      const hasAnyData = Object.values(data).some(
        (v) => typeof v === 'string' && v.trim().length > 0
      );
      if (hasAnyData) {
        iframeRef.current.contentWindow.postMessage(
          { type: 'injectData', data },
          '*'
        );
      }
    }
  };

  const muteCardAudio = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'mute' }, '*');
  };

  const unmuteCardAudio = () => {
    iframeRef.current?.contentWindow?.postMessage({ type: 'unmute' }, '*');
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [templateId]);

  useEffect(() => {
    if (!templateId) return;
    api.get(`/templates/${templateId}`)
      .then((data) => {
        setTemplate(data.template);
        setMusicOptions(data.music || []);
        setTemplateFields(data.fields || []);
        if (data.siteName) setSiteName(data.siteName);
        const initialData = {};
        (data.fields || []).forEach((f) => { initialData[f.fieldkey] = ''; });
        setTemplateData(initialData);
        const defaultMusic = (data.music || []).find((m) => m.isdefault);
        if (defaultMusic) setSelectedMusicId(String(defaultMusic.musicid));
      })
      .catch(() => setLoadError('Template not found'));
  }, [templateId]);

  useEffect(() => {
    if (!isLoggedIn) return;
    api.get('/moonbeams/usage')
      .then((data) => { setUsage(data.deliveries); })
      .catch((err) => { console.error('Failed to fetch usage:', err); });
  }, [isLoggedIn]);

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type !== 'templateData') return;

      const incoming = e.data.data || {};
      const current = templateDataRef.current || {};

      const incomingHasData = Object.values(incoming).some(
        (v) => typeof v === 'string' && v.trim().length > 0
      );
      const currentHasData = Object.values(current).some(
        (v) => typeof v === 'string' && v.trim().length > 0
      );

      if (!incomingHasData && currentHasData) {
        return;
      }

      setTemplateData(incoming);
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleStartSend = () => {
    setValidationError(null);
    const missingFields = templateFields
      .filter((f) => f.isrequired && !templateData[f.fieldkey]?.trim())
      .map((f) => f.label);
    if (missingFields.length > 0) {
      setValidationError(`Please fill in: ${missingFields.join(', ')}`);
      return;
    }
    window.scrollTo(0, 0);
    setMode('send');
    setSendStep(1);
  };

  const handleDeliveryModeChange = (newMode) => {
    if (newMode === 'birthday' && recipients.length > 0) {
      const withoutBirthday = recipients.filter((r) => !r.birthmonth || !r.birthdate);
      if (withoutBirthday.length > 0) {
        setBirthdaySwitchModal({ count: withoutBirthday.length, pendingMode: newMode });
        return;
      }
    }
    setDeliveryMode(newMode);
  };

  const handleBirthdaySwitchConfirm = () => {
    setRecipients((prev) => prev.filter((r) => r.birthmonth && r.birthdate));
    setDeliveryMode(birthdaySwitchModal.pendingMode);
    setBirthdaySwitchModal(null);
  };

  const handleBirthdaySwitchCancel = () => {
    setBirthdaySwitchModal(null);
  };

  // ─── Clipboard helper ───────────────────────────────────────────────────
  const copyToClipboard = async (text) => {
    if (!text) return false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch {
      // Fall through to legacy fallback below
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  // ─── Main share handler ─────────────────────────────────────────────────
  // Branches based on which channels are CONFIGURED. Unchanged behavior:
  // creates the moonbeam, creates the link (if enabled), sends/schedules
  // email (if enabled), writes the link to the clipboard, and navigates to
  // /my-moonbeams with a structured shareSuccess descriptor the modal reads.
  //
  // This is now reachable two ways:
  //   • Link-only: Step 1's "Share" button calls this directly (via
  //     onShareDirect), skipping the review screen.
  //   • Email (with or without link): Step 2's "Share" button calls this
  //     after the review screen, exactly as before.
  const handleSend = async () => {
    setError(null);

    const hasEmail = emailEnabled && recipients.length > 0;
    const hasLink = !!linkEnabled;

    // ── Validation ────────────────────────────────────────────────────────
    if (!hasEmail && !hasLink) {
      setError('Choose how to share — turn on the shareable link or turn on email and add recipients.');
      return;
    }
    if (hasEmail && deliveryMode === 'schedule' && (!scheduleDate || new Date(scheduleDate) <= new Date())) {
      setError('Please select a future date and time for the email send.');
      return;
    }
    if (hasEmail && deliveryMode === 'birthday') {
      const missing = recipients.filter((r) => !r.birthmonth || !r.birthdate);
      if (missing.length) { setError(`${missing.length} recipient(s) are missing a birthday`); return; }
    }
    if (hasEmail && usage && !isUnlimitedUsage(usage) && recipients.length > usage.remaining) {
      setError(`You can only send to ${usage.remaining} more recipient${usage.remaining !== 1 ? 's' : ''}. Remove ${recipients.length - usage.remaining} to continue.`);
      return;
    }

    const finalFromName = (fromName || '').trim() || defaultFromName || 'Someone Special';

    setSending(true);
    try {
      const formRecipientName = (templateData.recipient_name || '').trim();
      let cardRecipientName = null;
      if (hasEmail && recipients.length === 1) {
        cardRecipientName = recipients[0].name || formRecipientName || null;
      } else {
        cardRecipientName = formRecipientName || null;
      }

      // 1) Create the moonbeam record.
      const { moonbeam } = await moonbeamService.create({
        templateid: parseInt(templateId),
        recipientname: cardRecipientName,
        sendername: finalFromName,
        messagebody: templateData.message_1 || '',
        template_data: templateData,
        selectedmusicid: selectedMusicId ? parseInt(selectedMusicId) : null,
      });

      // 2) Create the shareable link FIRST when enabled.
      let shareUrl = null;
      if (hasLink) {
        const linkResp = await moonbeamService.createShareableLink(
          moonbeam.moonbeamid,
          linkLabel
        );
        shareUrl = linkResp?.link?.shareUrl || null;
      }

      // 3) Handle email channel.
      if (hasEmail) {
        if (deliveryMode === 'schedule') {
          await moonbeamService.schedule(moonbeam.moonbeamid, { scheduledLocalTime: scheduleDate, recipients });
        } else if (deliveryMode === 'birthday') {
          const birthdayRecipients = recipients.map((r) => ({ ...r, birthdaytime: birthdayTime }));
          await moonbeamService.schedule(moonbeam.moonbeamid, { scheduledsendat: null, deliverymode: 'birthday', birthdaytime: birthdayTime, recipients: birthdayRecipients });
        } else {
          await moonbeamService.send(moonbeam.moonbeamid, recipients);
        }
      }

      // 4) Write the shareable URL to the clipboard.
      let clipboardOk = false;
      if (shareUrl) {
        clipboardOk = await copyToClipboard(shareUrl);
      }

      // 5) Navigate to MyMoonbeams with a structured success descriptor.
      navigate('/my-moonbeams', {
        state: {
          shareSuccess: {
            linkUrl: shareUrl,
            linkLabel: hasLink ? (linkLabel || '').trim() : null,
            clipboardOk,

            emailRecipientCount: hasEmail ? recipients.length : 0,
            emailDeliveryMode: hasEmail ? deliveryMode : null,
            emailScheduleDate: hasEmail && deliveryMode === 'schedule' ? scheduleDate : null,
            emailBirthdayTime: hasEmail && deliveryMode === 'birthday' ? birthdayTime : null,
          },
        },
      });
    } catch (err) {
      const msg = err.data?.error || err.data?.message || 'Failed to share card';
      setError(msg === 'subscription_required' ? 'Upgrade your plan to share Moonbeams!' : msg);
    } finally {
      setSending(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // SHARE BUTTON - Determine text and behavior based on user state.
  // ═══════════════════════════════════════════════════════════════════════════
  const renderSendButton = () => {
    if (!isLoggedIn) {
      return (
        <a
          href="/login"
          className="flex items-center gap-1.5 px-4 sm:px-5 py-2 font-bold rounded-full text-sm transition-colors shadow-md flex-shrink-0 bg-gold text-royal-900 hover:bg-gold-light shadow-gold/20"
        >
          <span>Sign in to Share</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </a>
      );
    }

    if (!canSend) {
      return (
        <a
          href="/pricing"
          className="flex items-center gap-1.5 px-4 sm:px-5 py-2 font-bold rounded-full text-sm transition-colors shadow-md flex-shrink-0 bg-gold text-royal-900 hover:bg-gold-light shadow-gold/20"
        >
          <span>Upgrade to Share</span>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </a>
      );
    }

    return (
      <button
        onClick={handleStartSend}
        className="flex items-center gap-1.5 px-4 sm:px-5 py-2 font-bold rounded-full text-sm transition-colors shadow-md flex-shrink-0 bg-gold text-royal-900 hover:bg-gold-light shadow-gold/20"
      >
        <span>Share</span>
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </button>
    );
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="text-center py-20">
          <div className="text-4xl mb-4">✨</div>
          <p className="text-royal-800 dark:text-white text-lg">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-royal-300 dark:border-royal-600 border-t-gold rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CREATE MODE — Full-screen card preview with floating toolbar
  // ═══════════════════════════════════════════════════════════════════════════
  if (mode === 'create') {
    return (
      <div className="min-h-screen bg-royal-900">
        {/* Floating toolbar - overlays the iframe */}
        <div className="fixed top-14 sm:top-20 left-0 right-0 z-30 flex flex-col items-center pointer-events-none">
          <div className="inline-flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-1.5 sm:py-2.5 rounded-full bg-royal-900/90 backdrop-blur-md border border-royal-600/60 shadow-lg shadow-black/30 mt-2 pointer-events-auto">
            <a href="/moonbeams" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white border border-royal-600 hover:text-gold hover:border-gold/50 transition-colors flex-shrink-0">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
              <span>Different card</span>
            </a>

            {musicOptions.length > 0 && (
              <button onClick={() => setShowMusicModal(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors flex-shrink-0 ${
                  selectedMusicId ? 'bg-gold/15 border border-gold/40 text-gold hover:bg-gold/25' : 'border border-royal-600 text-white hover:text-gold hover:border-gold/50'
                }`}>
                <span>🎵</span>
                <span className="sm:hidden">Music</span>
                <span className="hidden sm:inline">{selectedMusicId ? (musicOptions.find(m => String(m.musicid) === String(selectedMusicId))?.name || 'Music') : 'Music'}</span>
              </button>
            )}

            {renderSendButton()}
          </div>
        </div>

        {showMusicModal && (
          <MusicSelectorModal
            value={selectedMusicId}
            onChange={(id) => setSelectedMusicId(id)}
            options={musicOptions}
            onClose={() => {
              unmuteCardAudio();
              setShowMusicModal(false);
            }}
            onPreviewStart={muteCardAudio}
            onPreviewStop={unmuteCardAudio}
          />
        )}

        {/* Iframe - uses calc to fill remaining viewport height. */}
        <iframe
          ref={iframeRef}
          srcDoc={previewHtml}
          title="Card Preview"
          className="w-full border-0 block"
          style={{ height: 'calc(100vh - 56px)' }}
          onLoad={handleIframeLoad}
        />

        {/* Validation error toast at bottom of screen */}
        {validationError && (
          <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 max-w-md w-[calc(100%-2rem)] px-4 py-3 bg-red-900/90 border border-red-700 rounded-xl shadow-xl text-red-100 text-sm">
            {validationError}
            <button
              onClick={() => setValidationError(null)}
              className="ml-3 text-red-200 hover:text-white"
              aria-label="Dismiss"
            >
              ✕
            </button>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // SEND MODE — 2-step flow (How to Share → Finalize & Share)
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      {/* Header — always dark */}
      <div className="relative overflow-hidden bg-royal-900">
        <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, rgba(244,208,63,0.05) 0%, transparent 50%)'
        }}></div>
        <div className="relative max-w-2xl mx-auto px-4 pt-6 sm:pt-10 pb-20 sm:pb-24 text-center">
          <button
            onClick={() => { window.scrollTo(0, 0); setMode('create'); setSendStep(1); setError(null); }}
            className="text-sm sm:text-base text-white hover:text-gold transition-colors flex items-center gap-1 mb-3 sm:mb-5 mx-auto w-fit"
          >
            <span>←</span><span>Back to card</span>
          </button>

          <h1 className="font-display text-2xl sm:text-3xl text-gold mb-2">Share Your Moonbeam</h1>
          <div className="flex items-center justify-center gap-4 mb-2">
            <div className="h-px w-10 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
            <span className="text-gold">✦</span>
            <div className="h-px w-10 bg-gradient-to-r from-transparent via-gold to-transparent"></div>
          </div>
          <p className="text-white/80 text-base italic">{template.name}</p>
        </div>

        {/* Wave curve */}
        <div className="absolute -bottom-px left-0 right-0 pointer-events-none">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block" preserveAspectRatio="none">
            <path
              d="M0 80L60 72C120 64 240 48 360 40C480 32 600 32 720 36C840 40 960 48 1080 52C1200 56 1320 56 1380 56L1440 56V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z"
              className="fill-cream dark:fill-bg-dark"
            />
          </svg>
        </div>
      </div>

      {/* Step content */}
      <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        <ProgressSteps currentStep={sendStep} />

        <div className="bg-royal-900 dark:bg-royal-800/20 border border-royal-700 dark:border-royal-700/40 rounded-xl p-3 sm:p-6">
          {sendStep === 1 && (
            <RecipientsAndTimingStep
              recipients={recipients}
              setRecipients={setRecipients}
              deliveryMode={deliveryMode}
              onDeliveryModeChange={handleDeliveryModeChange}
              scheduleDate={scheduleDate}
              setScheduleDate={setScheduleDate}
              birthdayTime={birthdayTime}
              setBirthdayTime={setBirthdayTime}
              tier={tier}
              isBirthdayTemplate={isBirthdayTemplate}
              isBirthdayDelivery={isBirthdayDelivery}
              canSend={canSend}
              isLoggedIn={isLoggedIn}
              usage={usage}
              disableGroups={!tier?.cangroups}
              siteName={siteName}
              templateName={template?.name || ''}
              // Shareable link state
              linkEnabled={linkEnabled}
              setLinkEnabled={setLinkEnabled}
              linkLabel={linkLabel}
              setLinkLabel={setLinkLabel}
              // Email channel state
              emailEnabled={emailEnabled}
              setEmailEnabled={setEmailEnabled}
              simpleEmailForm={simpleEmailForm}
              fromName={fromName}
              setFromName={setFromName}
              defaultFromName={defaultFromName}
              // Whether a share is currently in flight (link-only fast path
              // calls onShareDirect, which sets `sending`). Passed so Step 1
              // can disable its button while the send runs.
              sending={sending}
              // onNext → go to the review screen (Step 2). Used when email is
              // part of the share.
              onNext={() => setSendStep(2)}
              // onShareDirect → run the real send immediately, skipping the
              // review screen. Used for the link-only fast path.
              onShareDirect={handleSend}
              onBack={() => { window.scrollTo(0, 0); setMode('create'); setSendStep(1); }}
            />
          )}

          {sendStep === 2 && (
            <FinalizeStep
              templateData={templateData}
              recipients={recipients}
              deliveryMode={deliveryMode}
              scheduleDate={scheduleDate}
              birthdayTime={birthdayTime}
              canSend={canSend}
              isLoggedIn={isLoggedIn}
              sending={sending}
              onSend={handleSend}
              error={error}
              siteName={siteName}
              usage={usage}
              fromName={fromName}
              setFromName={setFromName}
              defaultFromName={defaultFromName}
              templateName={template?.name || ''}
              linkEnabled={linkEnabled}
              linkLabel={linkLabel}
              emailEnabled={emailEnabled}
              onBack={() => setSendStep(1)}
            />
          )}
        </div>

        {birthdaySwitchModal && (
          <BirthdaySwitchModal
            count={birthdaySwitchModal.count}
            onConfirm={handleBirthdaySwitchConfirm}
            onCancel={handleBirthdaySwitchCancel}
          />
        )}
      </div>
    </div>
  );
}